import React from "react";
import { Modal } from "react-bootstrap";
import { ICONS } from "./constants/swal.constants";
import "./CustomSwal.css";

interface CustomSwalProps {
  icon?: "success" | "error" | "warning" | "info";
  title?: string;
  text?: string;
  buttons?: boolean | string[] | { [key: string]: any };
  dangerMode?: boolean;
  onClose?: () => void;
}

interface CustomSwalStaticProps {
  fire: (props: CustomSwalProps) => Promise<any>;
  close: () => void;
}

// Static container to manage the modal state
let modalContainer: {
  show: (props: CustomSwalProps) => Promise<any>;
  hide: () => void;
} | null = null;

// Component that will be rendered
const CustomSwalComponent: React.FC = () => {
  const [visible, setVisible] = React.useState(false);
  const [props, setProps] = React.useState<CustomSwalProps>({});
  const [resolvePromise, setResolvePromise] = React.useState<
    ((value: any) => void) | null
  >(null);

  React.useEffect(() => {
    modalContainer = {
      show: (newProps) => {
        return new Promise((resolve) => {
          setProps(newProps);
          setVisible(true);
          setResolvePromise(() => resolve);
        });
      },
      hide: () => {
        setVisible(false);
      },
    };

    return () => {
      modalContainer = null;
    };
  }, []);

  const handleClose = (value?: any) => {
    setVisible(false);
    if (resolvePromise) resolvePromise(value);
    if (props.onClose) props.onClose();
  };

  const getIconSrc = () => {
    switch (props.icon) {
      case "success":
        return ICONS.roundCheck;
      case "error":
        return ICONS.error;
      case "warning":
        return ICONS.warning;
      case "info":
        return ICONS.info;
      default:
        return ICONS.info;
    }
  };

  const renderButtons = () => {
    // If buttons is true or not provided, show a single OK button
    if (props.buttons === undefined || props.buttons === true) {
      return (
        <button
          className="custom-swal-button-success"
          onClick={() => handleClose(true)}
        >
          OK
        </button>
      );
    }

    // If buttons is false, don't show any buttons
    if (props.buttons === false) {
      return null;
    }

    // If buttons is an array, show multiple buttons
    if (Array.isArray(props.buttons)) {
      return (
        <div className="custom-swal-buttons-container">
          {props.buttons.map((button, index) => (
            <button
              key={index}
              className={
                index === 0
                  ? "custom-swal-button"
                  : "custom-swal-button-success"
              }
              onClick={() => handleClose(index === 0 ? false : true)}
            >
              {button}
            </button>
          ))}
        </div>
      );
    }

    // Default to a single OK button
    return (
      <button
        className="custom-swal-button-success"
        onClick={() => handleClose(true)}
      >
        OK
      </button>
    );
  };

  return (
    <Modal show={visible} onHide={() => handleClose(false)} centered>
      <div
        className={`custom-swal-container ${
          props.dangerMode ? "danger-mode" : ""
        }`}
      >
        <div className="custom-swal-title-container">
          <img src={getIconSrc()} alt="" className="custom-swal-icon" />
          <p className="custom-swal-title">{props.title}</p>
        </div>
        {props.text && <p className="custom-swal-text">{props.text}</p>}
        {renderButtons()}
      </div>
    </Modal>
  );
};

// Create a singleton instance
const CustomSwalElement = document.createElement("div");
CustomSwalElement.id = "custom-swal-root";
document.body.appendChild(CustomSwalElement);

// Render the component
import ReactDOM from "react-dom";
ReactDOM.render(<CustomSwalComponent />, CustomSwalElement);

// Public API
const CustomSwal: CustomSwalStaticProps = {
  fire: (props) => {
    if (modalContainer) {
      return modalContainer.show(props);
    }
    return Promise.reject(new Error("Modal container not initialized"));
  },
  close: () => {
    if (modalContainer) {
      modalContainer.hide();
    }
  },
};

export default CustomSwal;
